<template>
  <ContentSection>
    <Container indent="lg-but-on-mobile-default">
      <div class="max-w-container-text">
        <div class="mb-6 space-y-2 sm:mb-9">
          <!-- HEADER -->
          <h2
            v-if="data.heading"
            v-animate-in-from-bottom
            class="typo-heading-lg sm:typo-heading-xl"
            v-html="data.heading"
          />
          <!-- TEXT -->
          <div
            v-if="data.text"
            v-animate-in-from-bottom
            class="typo-body-xl sm:typo-body-2xl"
            v-html="data.text"
          />
        </div>

        <!-- CHART -->
        <div v-if="data.chart_data" v-animate-in-from-bottom class="overflow-x-auto overflow-y-hidden">
          <div class="aspect-video max-h-[80vh] w-full" :class="{ 'max-lg:min-w-[40rem]': amountDataRows < 100, 'max-lg:min-w-[60rem]': amountDataRows >= 100 && amountDataRows < 1000, 'max-xl:min-w-container-max': amountDataRows >= 1000 && amountDataRows < 3000, 'max-2xl:min-w-[150rem]': amountDataRows >= 3000 }">
            <VChart
              ref="chartElement"
              :style="{
                maxHeight: '80vh',
                width: '100%',
                height: '100%',
              }"
              :option="option"
            />
          </div>
        </div>

        <ChartLegend
          v-if="data.chart_data"
          :series="series"
          :hidden-series="hiddenSeries"
          chart-type="bar"
          @toggle-series="toggleSeries($event)"
          @hover-series="hoverSeries($event)"
          @reset-series-opacity="resetSeriesOpacity()"
        />
      </div>
    </Container>
  </ContentSection>
</template>

<script lang="ts" setup>
import type { ComponentsMainChartBarFragment } from '#gql'

const props = defineProps<{
  data: Enforce<ComponentsMainChartBarFragment>

}>()

// Use localeProperties.iso if available, fallback to 'de-CH'
const { localeProperties } = useI18n()
const locale = localeProperties.value?.iso || 'de-CH'

const barConfigs = computed(() => {
  return props.data.bar_config
})

const amountDataRows = computed(() => {
  return props.data?.chart_data?.length || 0
})

const chartElement = ref<null | HTMLDivElement>(null)

const xAxis = computed(() => {
  if (!Array.isArray(props.data.chart_data) || !props.data.chart_data.length) {
    return []
  }
  return props.data.chart_data.slice(1).map(item => item.cells[0])
})

const series = computed(() => {
  if (
    !Array.isArray(props.data.chart_data)
    || props.data.chart_data.length <= 1
  ) {
    return []
  }

  const headers = props.data.chart_data[0].cells.slice(1) // Extract headers excluding the first column
  const dataRows = props.data.chart_data.slice(1) // Exclude the header row

  // Create a series for each column, excluding the first column
  return headers.map((header, index) => {
    const lines = {
      name: barConfigs.value[index]?.title || header,
      type: 'bar',
      data: dataRows.map((row) => {
        const value = row.cells[index + 1]
        return value ? parseFloat(String(value).replace(/[\s ]/g, '').replace(',', '.').replace('%', '')) : NaN
      }),
      barWidth: barConfigs.value[index]?.bar_width || 'auto',
      itemStyle: {
        color: barConfigs.value[index]?.bar_color, // Bar fill color
      },
      emphasis: {
        label: {
          show: true, // Show labels on hover
          position: 'top',
          fontSize: 14,
          fontWeight: 500,
          fontFamily: 'Avenir Next LT Pro, Helvetica, Arial, sans-serif',
          textAlign: 'center',
          formatter: (params) => {
            const { value } = params
            if (!props.data.unit) return value
            return props.data.unit_prefix
              ? `${props.data.unit} ${value}`
              : `${value}${props.data.unit}`
          },
        },
      },
    }

    return lines
  })
})

const option = ref<ECOption>({
  textStyle: {
    fontFamily: 'Avenir Next LT Pro, Helvetica, Arial, sans-serif', // Set global font
  },

  grid: {
    left: 0,
    right: 35, // right padding
    top: 15, // top padding
    bottom: 20, // bottom padding
    containLabel: true, // Ensure labels are contained within the grid
  },

  // Using custom legend component now
  // legend: {
  //   orient: 'horizontal',
  //   type: 'scroll', // Enable scrolling
  //   data: series.value.map(s => s?.name || 'undefined'),
  //   left: 0,
  //   bottom: 0, // Position at bottom
  //   textStyle: { // typo-body-lg
  //     fontSize: '16px', // 14px 0.875rem
  //     lineHeight: 20,
  //     fontWeight: 500,
  //     padding: [0, 30, 5, 4],
  //   },
  //   itemGap: 0, // Space between legend items
  //   itemWidth: 20, // Marker width
  //   itemHeight: 20, // Marker height (matches 3px border-bottom)
  // },

  tooltip: {
    trigger: 'item',
    backgroundColor: '#F3F7FF',
    borderWidth: 0,
    padding: [10, 12],
    extraCssText: 'border-radius: 0; box-shadow: none;',
    formatter: (params) => {
      const { seriesName, value, name, color, seriesIndex } = params
      const barConfig = barConfigs.value[seriesIndex] || {}

      const markerStyle = `
        display: inline-block;
        width: 10px;
        height: 10px;
        margin-right: 8px;
        margin-bottom: 4px;
        border-left: 10px solid ${color};
        border-radius: 20%;
        vertical-align: middle;
      `

      const formattedName = useFormatDate(seriesName, locale)
      const marker = `<span style="${markerStyle}"></span>`

      return `<span class="typo-body-lg">${marker}<strong>${formattedName}</strong><br/>${barConfig.text || ''}</span>`
    },

  },

  xAxis: {
    type: 'category',
    data: xAxis.value,
    offset: 10,
    axisLabel: {
      fontSize: '16px', // typo-body-lg 0.875rem
      lineHeight: 20,
      fontWeight: 500,
      rotate: props.data.rotate_x_axis_labels ? 60 : 0,
      formatter: (value) => {
        return useFormatDate(value, locale)
      },
    },
    boundaryGap: true,
  },

  yAxis: {
    type: 'value',
    axisLabel: {
      fontSize: '16px', // typo-body-lg 0.875rem
      lineHeight: 20,
      fontWeight: 500,
      padding: [0, 5, 0, 2], // Add padding
      formatter: (value) => {
        const valueFormatted = useFormatNumber(value)
        if (!props.data.unit) return valueFormatted
        return props.data.unit_prefix
          ? `${props.data.unit} ${valueFormatted}`
          : `${valueFormatted}${props.data.unit}`
      },
    },
    splitLine: {
      show: true, // Enable the horizontal lines
      lineStyle: {
        color: '#CCD7EE', // Line color
        width: 1, // Line width (thin)
        type: 'solid', // Line style: 'solid', 'dashed', 'dotted', etc.
      },
    },
    // splitNumber: 9, // Number of splits on the y-axis
  },

  series: series.value,

})

function hoverSeries(index: number) {
  chartElement.value?.chart.dispatchAction({
    type: 'highlight',
    seriesIndex: index,
  })
}

function resetSeriesOpacity() {
  chartElement.value?.chart.dispatchAction({
    type: 'downplay',
  })
}

const hiddenSeries = ref<number[]>([])

function toggleSeries(index: number) {
  if (hiddenSeries.value.includes(index)) {
    hiddenSeries.value = hiddenSeries.value.filter(i => i !== index)
  } else {
    hiddenSeries.value.push(index)
  }

  chartElement.value?.chart.dispatchAction({
    type: 'legendToggleSelect',
    seriesIndex: index,
  })

  option.value.series = series.value.map((serie, i) => ({
    ...serie,
    data: hiddenSeries.value.includes(i) ? [] : serie.data,
  }))
}

async function handleResize() {
  if (chartElement.value) {
    chartElement.value.resize() // Resize the chart instance
  }
}

const debouncedHandleResize = useDebounceFn(() => {
  handleResize()
}, 200)

onMounted(() => {
  window.addEventListener('resize', debouncedHandleResize)

  // Add legendselectchanged event handler
  if (chartElement.value?.chart) {
    chartElement.value.chart.on('legendselectchanged', (params) => {
      const { selected } = params // Object with series names and their selected state
      const visibleSeriesCount = Object.values(selected).filter(isSelected => isSelected).length

      // Prevent hiding the last visible series
      if (visibleSeriesCount === 0) {
        chartElement.value.chart.dispatchAction({
          type: 'legendSelect',
          name: params.name,
        })
      }
    })
  }
})

onBeforeUnmount(() => {
  // Clean up event listener and chart instance
  window.removeEventListener('resize', debouncedHandleResize)
})
</script>
